import styled, { createGlobalStyle } from "styled-components";
import { Flex, Box } from "rebass/styled-components";
import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

import { UserStateProvider } from "./userStore";
import { Dock } from "./MVVM/Components/Dock";
import { ClipQualitySettingsModalRenderer } from "./MVVM/Components/ClipQualitySettings/ClipQualitySettingsModalRenderer";
import { ModalManager } from "./MVVM/Components/ModalManager";
import { SiteNotice } from "./MVVM/Components/SiteNotice";
import { TrialCountdownBanner } from "./MVVM/Components/TrialCountdownBanner";
import { AppRoutes } from "./routes";
import { Navigation } from "./navigation";
import { Footer } from "./footer";
import { SiteHeader } from "./MVVM/Components/SiteHeader";
import { usePortal } from "MVVM/Hooks/usePortal";
import { ScrollRestoration } from "MVVM/Hooks/useScrollRestoration";
import { Wrapped } from "MVVM/Views/Wrapped";
import { SmartBanner } from "./MVVM/Components/SmartBanner";

// Main must never subscribe to user state (useUser / AllstarUser / any
// selector over it — recoil 0.1.x re-renders selector subscribers on every
// upstream write). Main re-rendering re-renders AppRoutes, which recreates
// every getChunkable Route component type, and React Router v5 remounts
// `component=` routes whenever that type identity changes — so any user write
// (settings toggles, profile refreshes) blows away the current page's state,
// e.g. closing the match-history panel mid-interaction. Components below Main
// subscribe individually; see src/MVVM/State/shellRemount.test.tsx.
const Main = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const game = urlParams.get("game");
  const location = useLocation();
  const {
    portalOpen: wrappedPortalOpen,
    setPortalOpen: setWrappedPortalOpen,
    Portal: WrappedPortal,
  } = usePortal(<Wrapped period="2024" />, "root");

  const wrappedEnabled = useMemo(() => {
    //eslint-disable-next-line
    const wrappedRegExp = /^\/wrapped\/2024\/[^\/\s]+$/g;

    return wrappedRegExp.test(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (wrappedEnabled) setWrappedPortalOpen(true);
  }, [setWrappedPortalOpen, wrappedEnabled]);

  if (game) localStorage.setItem("game", game);
  if (window.document.referrer)
    localStorage.setItem("initialReferrer", window.document.referrer);

  useEffect(() => {
    const segment = window.location.pathname.split("/")[1];
    document.body.classList.add(`page-${segment}`);
    return () => {
      document.body.classList.remove(`page-${segment}`);
    };
  }, [location]);

  return (
    <UserStateProvider>
      {wrappedPortalOpen && WrappedPortal}
      <GlobalStyle />
      <PageBackgroundStyle />
      {!location.pathname.match("/welcome") &&
        createPortal(<Dock />, document.body)}
      <LayeredContent>
        <SmartBanner
          title="Allstar.gg"
          appMeta={{
            ios: "6476161266",
            android: "com.androidapp.gg.allstar",
          }}
          deepLinks={{
            ios: "allstar://",
            android: "allstar://",
          }}
          position="top"
          button="Open"
          storeText={{
            ios: "Clip like the pros",
            android: "Clip like the pros",
          }}
        />
        <SiteHeader />
        <ColumnWrapper>
          <LeftColumn>
            <Navigation />
          </LeftColumn>
          <MainColumn>
            <SiteNotice />
            {/* Subscribes to user state internally — Main itself must stay
                free of user-state subscriptions (ALL-1634). */}
            <TrialCountdownBanner />
            {/* HUD/crosshair announcement retired. Re-mount AllstarClipsBanner
                here to bring it back. */}
            <SiteContent id="sitecontent">
              <MainContent id="main">
                {/* Scroll-to-top on navigation lives here now, so that
                    back/forward can restore instead of resetting. */}
                <ScrollRestoration />
                <AppRoutes />
                <Footer />
              </MainContent>
            </SiteContent>
          </MainColumn>
        </ColumnWrapper>
      </LayeredContent>
      <ModalManager />
      <ClipQualitySettingsModalRenderer />
    </UserStateProvider>
  );
};

const ColumnWrapper = styled(Flex)`
  position: relative;
  // Fills only what's left under the header. height: 100% overflowed body by
  // the header height, and focus/scrollIntoView then scrolled it out of reach.
  flex: 1;
  min-height: 0;
`;

const GlobalStyle = createGlobalStyle`
  html {
    @media (max-width: ${(props) => props.theme.breaks.narrow}) {
      font-size: 14px;
    }
  }
`;

const MainContent = styled(Box)`
  overflow-y: auto;
  width: 100%;
  &::-webkit-scrollbar {
    width: 12px; /* width of the entire scrollbar */
  }

  &::-webkit-scrollbar-track {
    background: #192832;
    border-left: 1px solid #20323f;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #253a49;
    border-radius: 4px;
    cursor: pointer;
  }
`;

const LeftColumn = styled(Flex)`
  flex-shrink: 0;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    height: 100%;
    position: absolute;
    z-index: 6;
  }
`;

const MainColumn = styled(Flex)`
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const SiteContent = styled(Flex)`
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const PageBackgroundStyle = createGlobalStyle`
  body {
    background-color: #1c2a34;
    background-position: top;
    background-size: contain;
    background-repeat: no-repeat;
  }
  
  .page-mobile-app-link {
    background: rgb(12,19,24);
  }

  .page-reset {
    background: linear-gradient(90deg, rgba(26,40,48,1) 0%, rgba(26,42,51,1) 47%, rgba(24,37,45,1) 100%);
  }

  .page-overwolf {
    background: #0b0c18;
  }
`;

const LayeredContent = styled(Flex)`
  flex-direction: column;
  width: 100%;

  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    flex-direction: column;
  }
`;

export { Main };
